'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useCommandPalette } from '@/context/CommandPaletteContext';
import { useClientManagement } from '@/hooks/useClientManagement';
import { useDraftManager } from '@/hooks/useDraftManager';
import { useSEOAnalysis } from '@/hooks/useSEOAnalysis';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/context/ChatContext';
import { useClient } from '@/context/ClientContext';

export interface Command {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  keywords: string[];
  category: 'navigation' | 'actions' | 'clients' | 'content' | 'settings';
  action: () => void | Promise<void>;
  disabled?: boolean;
  shortcut?: string;
}

export interface CommandCategory {
  name: string;
  commands: Command[];
}

export const useAllCommands = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { closePalette } = useCommandPalette();
  const { clients, createClient } = useClientManagement();
  const { saveDraft, clearDraft } = useDraftManager();
  const { analyzeContentSEO } = useSEOAnalysis();
  const { logOut } = useAuth();
  const { createNewConversation, loading, isCleaningUp } = useChat();
  const { activeClient, setActiveClient } = useClient();

  // Navigation Commands
  const navigationCommands: Command[] = useMemo(() => [
    {
      id: 'nav-home',
      name: 'Go to Chat',
      description: 'Navigate to main chat interface',
      icon: '💬',
      keywords: ['home', 'chat', 'main', 'conversations'],
      category: 'navigation',
      action: () => {
        router.push('/');
        closePalette();
      },
      disabled: pathname === '/'
    },
    {
      id: 'nav-clients',
      name: 'Go to Clients',
      description: 'View and manage your clients',
      icon: '👥',
      keywords: ['clients', 'customers', 'people', 'manage'],
      category: 'navigation',
      action: () => {
        router.push('/clients');
        closePalette();
      },
      disabled: pathname === '/clients'
    },
    {
      id: 'nav-tools',
      name: 'Go to PR Tools',
      description: 'Access PR and content tools',
      icon: '🛠️',
      keywords: ['tools', 'pr', 'press release', 'content', 'seo'],
      category: 'navigation',
      action: () => {
        router.push('/tools');
        closePalette();
      },
      disabled: pathname === '/tools'
    },
    {
      id: 'nav-content',
      name: 'Go to Content Library',
      description: 'Browse your content library',
      icon: '📚',
      keywords: ['content', 'library', 'documents', 'files'],
      category: 'navigation',
      action: () => {
        router.push('/content');
        closePalette();
      },
      disabled: pathname === '/content'
    },
    {
      id: 'nav-agents',
      name: 'Go to AI Agents',
      description: 'Manage AI agents and workflows',
      icon: '🤖',
      keywords: ['agents', 'ai', 'artificial intelligence', 'automation'],
      category: 'navigation',
      action: () => {
        router.push('/agents');
        closePalette();
      },
      disabled: pathname === '/agents'
    },
    {
      id: 'nav-profile',
      name: 'Go to Profile',
      description: 'View and edit your profile',
      icon: '👤',
      keywords: ['profile', 'account', 'user', 'personal'],
      category: 'navigation',
      action: () => {
        router.push('/profile');
        closePalette();
      },
      disabled: pathname === '/profile'
    },
    {
      id: 'nav-settings',
      name: 'Go to Settings',
      description: 'Configure application settings',
      icon: '⚙️',
      keywords: ['settings', 'preferences', 'config', 'options'],
      category: 'navigation',
      action: () => {
        router.push('/settings');
        closePalette();
      },
      disabled: pathname === '/settings'
    }
  ], [router, pathname, closePalette]);

  // Client Commands
  const clientCommands: Command[] = useMemo(() => {
    const baseCommands = [
      {
        id: 'client-new',
        name: 'Create New Client',
        description: 'Add a new client to your account',
        icon: '➕',
        keywords: ['new', 'client', 'add', 'create', 'customer'],
        category: 'clients',
        action: () => {
          router.push('/clients/new');
          closePalette();
        }
      }
    ];

    // Context-aware commands when a client is active
    if (activeClient) {
      baseCommands.push(
        {
          id: 'client-active-edit',
          name: `Edit ${activeClient.name}`,
          description: 'Edit the currently active client',
          icon: '✏️',
          keywords: ['edit', 'current', 'active', 'client', activeClient.name.toLowerCase()],
          category: 'clients',
          action: () => {
            router.push(`/clients/${activeClient.id}/edit`);
            closePalette();
          }
        },
        {
          id: 'client-active-press-release',
          name: `Create Press Release for ${activeClient.name}`,
          description: 'Generate a press release for the active client',
          icon: '📰',
          keywords: ['press', 'release', 'current', 'client', activeClient.name.toLowerCase()],
          category: 'clients',
          action: () => {
            router.push(`/tools?tab=press-release&client=${activeClient.id}`);
            closePalette();
          }
        },
        {
          id: 'client-clear-active',
          name: 'Clear Active Client',
          description: 'Remove current active client selection',
          icon: '🚫',
          keywords: ['clear', 'remove', 'active', 'client'],
          category: 'clients',
          action: () => {
            setActiveClient(null);
            closePalette();
          }
        }
      );
    }

    // Recent clients (up to 6 to not overwhelm)
    const recentClients = clients.slice(0, 6).map(client => ({
      id: `client-${client.id}`,
      name: `${client.id === activeClient?.id ? '• ' : ''}${client.name}`,
      description: `View ${client.name}'s profile and details${client.id === activeClient?.id ? ' (currently active)' : ''}`,
      icon: client.id === activeClient?.id ? '📍' : '👤',
      keywords: ['client', client.name.toLowerCase(), 'view', 'profile'],
      category: 'clients' as const,
      action: () => {
        if (client.id !== activeClient?.id) {
          setActiveClient(client);
        }
        router.push(`/clients/${client.id}`);
        closePalette();
      }
    }));

    return [...baseCommands, ...recentClients];
  }, [clients, activeClient, setActiveClient, router, closePalette]);

  // Content & Action Commands  
  const actionCommands: Command[] = useMemo(() => {
    const baseActions = [
      {
        id: 'new-conversation',
        name: 'New Conversation',
        description: 'Start a new chat conversation',
        icon: '💬',
        keywords: ['new', 'conversation', 'chat', 'start', 'fresh'],
        category: 'actions',
        shortcut: 'Ctrl+N',
        action: async () => {
          if (loading || isCleaningUp) return; // Prevent creation during deletion
          console.log('🚨 Ctrl+N triggered', { isCleaningUp, loading }); // Debug logging
          try {
            await createNewConversation();
            router.push('/');
            closePalette();
          } catch (error) {
            console.error('Failed to create new conversation:', error);
          }
        }
      },
      {
        id: 'save-draft',
        name: 'Save Draft',
        description: 'Save current content as draft',
        icon: '💾',
        keywords: ['save', 'draft', 'backup', 'store'],
        category: 'content',
        shortcut: 'Ctrl+S',
        action: () => {
          // This would save current content
          console.log('Draft saved');
          closePalette();
        },
        disabled: pathname === '/clients' // Not applicable on clients list page
      },
      {
        id: 'clear-draft',
        name: 'Clear Draft',
        description: 'Clear current draft content',
        icon: '🗑️',
        keywords: ['clear', 'delete', 'draft', 'remove'],
        category: 'content',
        action: () => {
          clearDraft();
          closePalette();
        },
        disabled: pathname === '/clients' // Not applicable on clients list page
      }
    ];

    // Context-aware content generation commands
    const contentCommands = [
      {
        id: 'press-release',
        name: activeClient ? `Press Release for ${activeClient.name}` : 'Generate Press Release',
        description: activeClient ? `Create a press release for ${activeClient.name}` : 'Create a new press release',
        icon: '📰',
        keywords: ['press', 'release', 'news', 'generate', 'pr'],
        category: 'content',
        action: () => {
          const url = activeClient 
            ? `/tools?tab=press-release&client=${activeClient.id}`
            : '/tools?tab=press-release';
          router.push(url);
          closePalette();
        }
      },
      {
        id: 'media-pitch',
        name: activeClient ? `Media Pitch for ${activeClient.name}` : 'Generate Media Pitch',
        description: activeClient ? `Create a media pitch for ${activeClient.name}` : 'Create a media pitch',
        icon: '📧',
        keywords: ['media', 'pitch', 'email', 'journalist', 'outreach'],
        category: 'content',
        action: () => {
          const url = activeClient 
            ? `/tools?tab=media-pitch&client=${activeClient.id}`
            : '/tools?tab=media-pitch';
          router.push(url);
          closePalette();
        }
      }
    ];

    // Add SEO analysis command if on tools page
    if (pathname === '/tools') {
      baseActions.push({
        id: 'analyze-seo',
        name: 'Analyze SEO',
        description: 'Run SEO analysis on current content',
        icon: '🔍',
        keywords: ['seo', 'analyze', 'analysis', 'optimize', 'search'],
        category: 'content',
        action: async () => {
          // This would trigger SEO analysis on current content
          router.push('/tools?tab=seo');
          closePalette();
        }
      });
    }

    return [...baseActions, ...contentCommands];
  }, [createNewConversation, loading, isCleaningUp, clearDraft, router, closePalette, pathname, activeClient]);

  // Settings Commands
  const settingsCommands: Command[] = useMemo(() => [
    {
      id: 'logout',
      name: 'Logout',
      description: 'Sign out of your account',
      icon: '🚪',
      keywords: ['logout', 'sign out', 'exit', 'leave'],
      category: 'settings',
      action: async () => {
        try {
          await logOut();
          closePalette();
        } catch (error) {
          console.error('Failed to logout:', error);
        }
      }
    }
  ], [logOut, closePalette]);

  // Combine all commands
  const allCommands = useMemo(() => [
    ...navigationCommands,
    ...clientCommands,
    ...actionCommands,
    ...settingsCommands
  ], [navigationCommands, clientCommands, actionCommands, settingsCommands]);

  // Group commands by category
  const commandsByCategory = useMemo((): CommandCategory[] => [
    {
      name: 'Navigation',
      commands: navigationCommands
    },
    {
      name: 'Actions',
      commands: actionCommands
    },
    {
      name: 'Clients',
      commands: clientCommands
    },
    {
      name: 'Settings',
      commands: settingsCommands
    }
  ], [navigationCommands, actionCommands, clientCommands, settingsCommands]);

  return {
    commands: allCommands,
    commandsByCategory,
    navigationCommands,
    clientCommands,
    actionCommands,
    settingsCommands
  };
};